import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {YesNoModal} from '../../elements/YesNoModal';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {DocumentRawModal} from './DocumentRawModal';
import {DocumentKindModal} from './DocumentKindModal';
import {DocumentDeleteModal} from './DocumentDeleteModal';
import {deletedMessage, deleteErrorMessage} from './deleteMessages';
import {ContentGapsBadge} from './ContentGapsBadge';
import {kindLabelKey} from './contextPackConstants';
import {
    DOC_STATUS, DOC_SOURCE, DOC_STATUS_MODIFIER, REFRESH_CONFIRM_REASONS, URL_ERROR_KEYS,
    docStatusLabelKey, docSourceLabelKey, docErrorParts,
} from './docConstants';
import {fileKindFaIcon, fileKindOf} from './ingestFiles';

// Vista "Documentos": el crudo del cliente (agency-only). Tabla con estado de extracción,
// cantidad de hechos generados, ver-crudo (drawer read-only), reintentar (FAILED), actualizar
// desde la URL (documentos cargados desde un enlace) y borrar.
class MemoryDocumentsViewClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            documents: [],
            factsCountByDoc: {},
            loading: true,
            busyId: null,
            // Qué acción está en vuelo en esa fila: las dos comparten el disabled, pero sólo
            // el refresh (que hace I/O de red sincrónico) cambia su label mientras corre.
            busyAction: null,
            rawTarget: null,
            deleteTarget: null,
            // Refresh frenado por un guard del backend, a la espera de que el usuario decida.
            confirmTarget: null,
            deleting: false,
            kindTarget: null,
            savingKind: false,
            // Documento que el usuario quiere volver a procesar, a la espera de que confirme. La
            // confirmación existe por dos razones concretas (gasto de IA y hechos duplicados), no
            // como un «¿estás seguro?» de trámite.
            reprocessTarget: null,
        };
        this.handleRetry = this.handleRetry.bind(this);
        this.handleReprocessConfirm = this.handleReprocessConfirm.bind(this);
        this.handleRefreshUrl = this.handleRefreshUrl.bind(this);
        this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
        this.handleKindConfirm = this.handleKindConfirm.bind(this);
    }

    componentDidMount() {
        this.fetchAll();
    }

    // Material nuevo entrado por la Barra Total del Recorrido, que vive en esta misma pantalla.
    // `refreshToken` sube con cada ingesta. Va en silencio (sin spinner): la lista ya está en
    // pantalla y lo único que cambia es que aparece una fila más — un `loading` acá haría
    // parpadear todo lo que el usuario está mirando.
    componentDidUpdate(prevProps) {
        if (prevProps.refreshToken !== this.props.refreshToken) this.fetchAll(true);
    }

    componentWillUnmount() {
        this._clearPoll();
    }

    _clearPoll() {
        if (this._pollTimer) {
            clearTimeout(this._pollTimer);
            this._pollTimer = null;
        }
    }

    // Mientras haya documentos PENDING/EXTRACTING (extracción en curso), refresca en silencio
    // para que el estado y el conteo de hechos aparezcan solos cuando termina (sin F5).
    //
    // `poll={false}` lo usa el Recorrido: ahí esta tabla convive en el MISMO acto con la lista de
    // ingesta, que muestra esos estados en vivo y ya pollea este endpoint. Dos pollers al mismo
    // endpoint en la misma pantalla es pagar dos veces por el dato — pero el que se apaga tiene que
    // ser éste, no el otro: apagar el de la lista de ingesta dejaba las filas clavadas en "en cola"
    // para siempre y al narrador mudo, porque sus estados salen justamente de esa vuelta de poll.
    // Quien apaga el poll se hace cargo de refrescar por `refreshToken`.
    _schedulePollIfNeeded() {
        this._clearPoll();
        if (this.props.poll === false) return;
        const inFlight = this.state.documents.some(
            (d) => d.status === DOC_STATUS.PENDING || d.status === DOC_STATUS.EXTRACTING);
        if (inFlight) {
            this._pollTimer = setTimeout(() => this.fetchAll(true), 8000);
        }
    }

    fetchAll(silent = false) {
        const {t} = this.props;
        const clientId = this.props.clientId;
        if (!silent) this.setState({loading: true});
        Promise.all([
            ClientMemoryApi.listDocuments(clientId),
            ClientMemoryApi.listFacts(clientId),
        ]).then(([docsResp, factsResp]) => {
            // Los listados devuelven el array directo ({documents}/{facts}), sin {status}.
            if (!docsResp || !Array.isArray(docsResp.documents)) {
                this.setState({loading: false});
                if (!silent) toast.error(t('client_memory.toast.load_error'));
                return;
            }
            const factsCountByDoc = {};
            if (factsResp && Array.isArray(factsResp.facts)) {
                factsResp.facts.forEach((f) => {
                    if (f.source_document_id) {
                        factsCountByDoc[f.source_document_id] = (factsCountByDoc[f.source_document_id] || 0) + 1;
                    }
                });
            }
            this.setState({documents: docsResp.documents, factsCountByDoc, loading: false},
                () => this._schedulePollIfNeeded());
        }).catch(() => {
            this.setState({loading: false});
            if (!silent) toast.error(t('client_memory.toast.load_error'));
        });
    }

    _reprocessQuestion() {
        // El nombre del documento va DENTRO de la pregunta (QA 2026-09-07, QA-VIS-cola-03): la tabla
        // muestra cinco «Volver a procesar» idénticos a 30 px uno del otro, y la acción gasta crédito
        // de IA. Un diálogo que no nombra su objeto no deja darse cuenta de que se apuntó mal.
        // El título lo escribe quien subió el material: va interpolado con el escape de i18next
        // ACTIVO (el default), así que no hace falta desactivarlo acá.
        const {t} = this.props;
        const doc = this.state.reprocessTarget;
        if (!doc) return '';
        const key = doc.is_audio
            ? 'client_memory.documents.reprocess_question_audio'
            : 'client_memory.documents.reprocess_question';
        return t(key, {title: doc.title || t('client_memory.documents.untitled')});
    }

    renderDocError(doc) {
        const {t} = this.props;
        const {message, technical} = docErrorParts(doc, t);
        // El volcado del motor no se muestra, pero tampoco se tira: queda detrás de un gesto para
        // que soporte lo tenga (QA 2026-09-07, QA-VIS-cola-06).
        return (
            <div className="cmem-doc-error">
                {message}
                {technical && (
                    <details className="cmem-doc-error-detail">
                        <summary>{t('client_memory.doc_error_detail')}</summary>
                        <code>{technical}</code>
                    </details>
                )}
            </div>
        );
    }

    handleReprocessConfirm() {
        const {t} = this.props;
        const doc = this.state.reprocessTarget;
        if (!doc) return;
        this.setState({busyId: doc.id, busyAction: 'reprocess', reprocessTarget: null});
        ClientMemoryApi.reprocessDocument(doc.id, this.props.clientId)
            .then((data) => {
                this.setState({busyId: null, busyAction: null});
                if (data && data.status === 'OK') {
                    this.setState((prev) => ({
                        documents: prev.documents.map((d) => (d.id === doc.id ? data.data : d)),
                    }), () => this._schedulePollIfNeeded());
                    toast.success(t('client_memory.documents.reprocess_ok'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({busyId: null, busyAction: null});
                toast.error(t('client_memory.toast.error'));
            });
    }

    handleRetry(doc) {
        const {t} = this.props;
        this.setState({busyId: doc.id, busyAction: 'retry'});
        ClientMemoryApi.retryDocument(doc.id, this.props.clientId)
            .then((data) => {
                this.setState({busyId: null, busyAction: null});
                if (data && data.status === 'OK') {
                    this.setState((prev) => ({
                        documents: prev.documents.map((d) => (d.id === doc.id ? data.data : d)),
                    }), () => this._schedulePollIfNeeded());
                    // `queued`: la tarea sigue en la cola y no se reintentó nada.
                    if (data.queued) toast.info(t('client_memory.documents.retry_still_queued'));
                    else toast.success(t('client_memory.documents.retry_ok'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({busyId: null, busyAction: null});
                toast.error(t('client_memory.toast.error'));
            });
    }

    // Vuelve a leer la página de un documento cargado desde un enlace. Va sin confirmación
    // previa (como Reintentar) porque el caso normal no destruye nada: si la página no cambió
    // no pasa nada, y si cambió, la re-extracción preserva la curación humana. La excepción es
    // que la página que contesta no sea la que se venía leyendo (un challenge del sitio, un muro
    // de login): eso sí pisaría el crudo, así que el backend lo frena —por tamaño o por destino—
    // y recién ahí se pregunta, con el hecho concreto a la vista, que es lo único que permite
    // decidir. `force` reintenta salteando ese guard. Cinco desenlaces, cinco respuestas
    // distintas: un aviso único no deja distinguir "se está actualizando" de "no había nada
    // que traer".
    handleRefreshUrl(doc, force = false) {
        const {t} = this.props;
        this.setState({busyId: doc.id, busyAction: 'refresh'});
        ClientMemoryApi.refreshUrlDocument(doc.id, this.props.clientId, force)
            .then((data) => {
                this.setState({busyId: null, busyAction: null, confirmTarget: null});
                if (data && data.status === 'OK') {
                    this.setState((prev) => ({
                        documents: prev.documents.map((d) => (d.id === doc.id ? data.data : d)),
                    }), () => this._schedulePollIfNeeded());
                    toast.success(t('client_memory.documents.refresh_url_ok'));
                } else if (data && data.status === 'UNCHANGED') {
                    toast.info(t('client_memory.documents.refresh_url_unchanged'));
                } else if (data && REFRESH_CONFIRM_REASONS[data.reason_code]) {
                    this.setState({confirmTarget: {doc, data}});
                } else {
                    // El motivo del rechazo (página caída, pide login, se hizo enorme) es
                    // accionable: muere en el toast genérico si no se mapea.
                    const reasonKey = data && URL_ERROR_KEYS[data.reason_code];
                    toast.error(t(reasonKey || 'client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({busyId: null, busyAction: null, confirmTarget: null});
                toast.error(t('client_memory.toast.error'));
            });
    }

    // Traduce el rechazo del backend a la pregunta que corresponde. Cada motivo trae SUS datos
    // (los dos tamaños, o la dirección nueva): sin ellos la pregunta sería una advertencia
    // genérica y el usuario no tendría con qué decidir.
    _refreshConfirmText() {
        const {t} = this.props;
        const target = this.state.confirmTarget;
        if (!target) return {title: '', question: ''};
        const {data} = target;
        const keys = REFRESH_CONFIRM_REASONS[data.reason_code];
        const values = data.reason_code === 'URL_REDIRECTED'
            ? {url: data.final_url_after}
            : {
                before: (data.chars_before || 0).toLocaleString(),
                after: (data.chars_after || 0).toLocaleString(),
            };
        return {
            title: t(keys.title),
            // El destino nuevo lo elige un tercero: va como valor interpolado y sin
            // `escapeValue:false`, así que i18next lo escapa y React lo pinta como texto.
            question: t(keys.question, values),
        };
    }

    // Avisa al padre que la BIBLIOTECA cambió de forma (se borró un documento o se re-etiquetó
    // uno), no que se refrescó la lista. Lo escucha el Paquete de Contexto, que cuenta documentos
    // por tipo: re-etiquetar es literalmente la acción que tacha un ítem del checklist, y borrar
    // puede devolver uno a "falta". El `refreshToken` que baja de arriba sólo cubre la INGESTA, así
    // que sin esto el strip seguía mostrando el conteo viejo con el panel abierto.
    // Prop opcional: sin ella la vista se comporta igual que siempre.
    notifyDocumentsChanged() {
        if (this.props.onDocumentsChanged) this.props.onDocumentsChanged();
    }

    // `withExtracted` lo decide el diálogo: borrar sólo el material, o también los hechos, el
    // resumen y los eventos que salieron de él (card VLtk3vc7).
    handleDeleteConfirm(withExtracted) {
        const {t} = this.props;
        const doc = this.state.deleteTarget;
        if (!doc) return;
        this.setState({deleting: true});
        ClientMemoryApi.deleteDocument(doc.id, this.props.clientId, withExtracted)
            .then((data) => {
                this.setState({deleting: false, deleteTarget: null});
                if (data && data.status === 'OK') {
                    this.setState((prev) => ({documents: prev.documents.filter((d) => d.id !== doc.id)}));
                    this.notifyDocumentsChanged();
                    toast.success(deletedMessage(t, withExtracted, data.data));
                } else {
                    toast.error(deleteErrorMessage(t, data));
                }
            })
            .catch(() => {
                this.setState({deleting: false, deleteTarget: null});
                toast.error(t('client_memory.toast.error'));
            });
    }

    handleKindConfirm(kind) {
        const {t} = this.props;
        const doc = this.state.kindTarget;
        if (!doc) return;
        this.setState({savingKind: true});
        ClientMemoryApi.patchDocumentKind(doc.id, this.props.clientId, kind)
            .then((data) => {
                this.setState({savingKind: false});
                if (data && data.status === 'OK') {
                    // El PATCH devuelve el documento con el MISMO serializer que el listado, así que
                    // se reemplaza la fila entera en vez de parchearle el kind: la fila no puede
                    // divergir de lo que el backend acaba de guardar.
                    this.setState((prev) => ({
                        documents: prev.documents.map((d) => (d.id === doc.id ? data.data : d)),
                        kindTarget: null,
                    }));
                    this.notifyDocumentsChanged();
                    toast.success(t('client_memory.documents.kind_ok'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({savingKind: false});
                toast.error(t('client_memory.toast.error'));
            });
    }

    _dateLabel(doc) {
        const value = doc.occurred_on || (doc.created_on ? doc.created_on.slice(0, 10) : null);
        return value || '—';
    }

    // El tipo alimenta el checklist del Paquete de Contexto: mostrarlo (y dejar corregirlo) es lo
    // que hace visible que un documento está contando para el ítem equivocado. Sin clasificar =
    // todavía se está extrayendo, o es un documento viejo anterior a la clasificación.
    renderKindBadge(doc) {
        const {t} = this.props;
        const label = doc.document_kind
            ? t(kindLabelKey(doc.document_kind))
            : t('client_memory.documents.kind_none');
        // `aria-label` y no `title=""`: la convención del repo reserva el tooltip para el patrón
        // `warning-tooltip`, y acá no hace falta uno — el lápiz ya dice que se puede editar.
        return (
            <button type="button"
                    className={`cmem-doc-kind ${doc.document_kind ? '' : '-none'}`}
                    aria-label={`${t('client_memory.documents.change_kind')}: ${label}`}
                    onClick={() => this.setState({kindTarget: doc})}>
                {label}
                <FaIcon iconName="pencil-alt" className="cmem-doc-kind-edit"/>
            </button>
        );
    }

    renderEmpty() {
        const {t} = this.props;
        return (
            <div className="cmem-empty">
                <FaIcon iconName="file-lines" className="cmem-empty-icon"/>
                <h3>{t('client_memory.documents.empty_title')}</h3>
                <p>{t('client_memory.documents.empty_subtitle')}</p>
                <div className="cmem-empty-actions">
                    <Link className="btn -alt cmem-empty-cta"
                          to={`/client/${this.props.clientId}/memory/setup`}>
                        <FaIcon iconName="cloud-arrow-up"/>
                        {t('client_memory.empty.cta')}
                    </Link>
                </div>
            </div>
        );
    }

    // Un archivo subido trae `file` (`{name, mime_type, size}`): el ícono por tipo sale de ahí, por
    // PRESENCIA — sin el campo (backend viejo, otro origen) no se dibuja nada; nunca se adivina por
    // el título.
    renderFileIcon(doc) {
        const file = doc.file;
        if (!file || !file.name) return null;
        return (
            <span className="cmem-doc-file-icon" aria-hidden="true">
                <FaIcon iconName={fileKindFaIcon(fileKindOf(file.name, file.mime_type))}/>
            </span>
        );
    }

    renderRow(doc) {
        const {t} = this.props;
        const isFailed = doc.status === DOC_STATUS.FAILED;
        // La lectura se interrumpió (el backend lo deduce por el tiempo en EXTRACTING): no es un
        // fallo del material, y el retry SÍ lo acepta. Sin esto la fila decía «Extrayendo» para
        // siempre, sin ninguna puerta.
        const isStale = doc.stale === true;
        const isUrlDoc = doc.source === DOC_SOURCE.URL;
        // EXTRACTING el backend lo rechaza (pisaría el crudo bajo los pies del extractor);
        // PENDING lo acepta, pero un documento recién subido está en cola unos segundos y
        // ofrecer ahí "actualizar" es ruido — mejor esperar a que termine.
        const inFlight = doc.status === DOC_STATUS.PENDING || doc.status === DOC_STATUS.EXTRACTING;
        const busy = this.state.busyId === doc.id;
        const refreshing = busy && this.state.busyAction === 'refresh';
        const factCount = this.state.factsCountByDoc[doc.id] || 0;
        return (
            <tr key={doc.id}>
                <td>
                    <div className="cmem-doc-title">
                        {this.renderFileIcon(doc)}
                        {doc.title}
                    </div>
                    {doc.file && doc.file.name && doc.file.name !== doc.title && (
                        <div className="cmem-doc-file">{doc.file.name}</div>
                    )}
                    <div className="cmem-doc-tags">
                        <span className="cmem-doc-source">{t(docSourceLabelKey(doc.source))}</span>
                        {this.renderKindBadge(doc)}
                        {/* Va acá y no en la celda de estado a propósito: el documento se extrajo
                            BIEN (queda «Listo»), lo que está incompleto es su texto. Mezclarlo con
                            el estado lo haría leer como una falla de la extracción. */}
                        <ContentGapsBadge document={doc}/>
                        {/* Antes de actualizar hay que poder mirar la página: el link a la
                            fuente es lo que hace informada la decisión. */}
                        {isUrlDoc && doc.web_view_link && (
                            <a className="cmem-doc-weblink" href={doc.web_view_link}
                               target="_blank" rel="noopener noreferrer">
                                {t('client_memory.documents.open_page')}
                                <FaIcon iconName="up-right-from-square" className="cmem-source-doc-ext"/>
                            </a>
                        )}
                    </div>
                </td>
                <td className="cmem-doc-date">{this._dateLabel(doc)}</td>
                <td>
                    <span className={`cmem-doc-status ${DOC_STATUS_MODIFIER[doc.status] || '-pending'}`}>
                        {t(docStatusLabelKey(doc.status))}
                    </span>
                    {isFailed && doc.summary_error && this.renderDocError(doc)}
                    {isStale && !isFailed && (
                        <div className="cmem-doc-error">{t('client_memory.doc_stale')}</div>
                    )}
                </td>
                <td className="cmem-doc-num">{(doc.char_count || 0).toLocaleString()}</td>
                <td className="cmem-doc-num">{doc.status === DOC_STATUS.READY ? factCount : '—'}</td>
                <td className="cmem-doc-actions">
                    <button type="button" className="cmem-link"
                            onClick={() => this.setState({rawTarget: doc})}>
                        {t('client_memory.documents.view_raw')}
                    </button>
                    {/* `can_retry === false` = el backend ya sabe que reintentar da lo mismo (el
                        material no aporta nada, el audio no tenía voz, el blob no está). Hasta el
                        2026-09-04 la fila mostraba las dos cosas juntas: el motivo diciendo
                        «Reintentar no cambia el resultado» y, al lado, el botón. Con el campo
                        ausente —backend viejo— se conserva el comportamiento anterior. */}
                    {(isFailed || isStale) && doc.can_retry !== false && (
                        <button type="button" className="cmem-link" disabled={busy}
                                onClick={() => this.handleRetry(doc)}>
                            {t('client_memory.documents.retry')}
                        </button>
                    )}
                    {/* «Volver a procesar»: el gemelo de «Reintentar» para un documento SANO. El
                        backend decide cuándo se ofrece (`can_reprocess`): sólo un READY cuyo
                        material se pueda volver a conseguir. Nunca conviven los dos botones. */}
                    {doc.can_reprocess && (
                        <button type="button" className="cmem-link" disabled={busy}
                                onClick={() => this.setState({reprocessTarget: doc})}>
                            {t('client_memory.documents.reprocess')}
                        </button>
                    )}
                    {isUrlDoc && (
                        <button type="button" className="cmem-link" disabled={busy || inFlight}
                                onClick={() => this.handleRefreshUrl(doc)}>
                            {/* El fetch de la página es sincrónico y puede tardar varios
                                segundos: un botón sólo deshabilitado se lee como que no pasó
                                nada. El label dice que está trabajando. */}
                            {refreshing
                                ? t('client_memory.documents.refresh_url_busy')
                                : t('client_memory.documents.refresh_url')}
                        </button>
                    )}
                    <button type="button" className="cmem-link-danger"
                            onClick={() => this.setState({deleteTarget: doc})}>
                        {t('client_memory.action.delete')}
                    </button>
                </td>
            </tr>
        );
    }

    render() {
        const {t} = this.props;
        if (this.state.loading) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        if (this.state.documents.length === 0) {
            return <div className="cmem-documents">{this.renderEmpty()}</div>;
        }

        return (
            <div className="cmem-documents">
                {/* `embedded` (vista Fuentes de la ficha v2): el bloque padre ya pone título y CTA —
                    sin header interno (dejaba la pill "1" suelta + un botón gigante). */}
                {!this.props.embedded && (
                    <header className="cmem-section-head">
                        <h3>{t('client_memory.documents.title')}</h3>
                        <span className="cmem-count-pill">{this.state.documents.length}</span>
                        <Link className="btn -negative cmem-sm cmem-add-btn"
                              to={`/client/${this.props.clientId}/memory/setup`}>
                            <FaIcon iconName="plus"/> {t('client_memory.documents.upload')}
                        </Link>
                    </header>
                )}

                <table className="cmem-doc-table">
                    <thead>
                        <tr>
                            <th>{t('client_memory.documents.col_document')}</th>
                            <th>{t('client_memory.documents.col_date')}</th>
                            <th>{t('client_memory.documents.col_status')}</th>
                            <th className="cmem-doc-num">{t('client_memory.documents.col_chars')}</th>
                            <th className="cmem-doc-num">{t('client_memory.documents.col_facts')}</th>
                            <th>{t('client_memory.documents.col_actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.documents.map((doc) => this.renderRow(doc))}
                    </tbody>
                </table>

                <DocumentRawModal
                    isOpen={!!this.state.rawTarget}
                    document={this.state.rawTarget}
                    clientId={this.props.clientId}
                    toggle={() => this.setState({rawTarget: null})}
                />
                <DocumentKindModal
                    isOpen={!!this.state.kindTarget}
                    doc={this.state.kindTarget}
                    isSaving={this.state.savingKind}
                    toggle={() => this.setState({kindTarget: null})}
                    onConfirm={this.handleKindConfirm}
                />
                <DocumentDeleteModal
                    isOpen={!!this.state.deleteTarget}
                    doc={this.state.deleteTarget}
                    clientId={this.props.clientId}
                    isSaving={this.state.deleting}
                    toggle={() => this.setState({deleteTarget: null})}
                    onConfirm={this.handleDeleteConfirm}
                />
                {/* La pregunta dice las DOS consecuencias, no «¿estás seguro?»: que se vuelve a
                    gastar crédito de IA (y en audio, dos veces) y que pueden aparecer hechos
                    duplicados. Y dice también lo que NO se pierde, que es lo que un usuario
                    necesita saber para animarse. */}
                <YesNoModal
                    skipRenderButton
                    className="cmem-confirm-costly"
                    isOpen={!!this.state.reprocessTarget}
                    title={t('client_memory.documents.reprocess_title')}
                    question={this._reprocessQuestion()}
                    isSaving={!!this.state.reprocessTarget
                        && this.state.busyId === this.state.reprocessTarget.id}
                    onYesAnswer={this.handleReprocessConfirm}
                    onNoAnswer={() => this.setState({reprocessTarget: null})}
                />
                {/* Sólo aparece cuando el backend frenó el refresh: la pregunta lleva el dato
                    del rechazo porque el usuario es el único que sabe si su página se acortó o
                    se mudó de verdad, o si el sitio le devolvió un bloqueo. */}
                <YesNoModal
                    skipRenderButton
                    isOpen={!!this.state.confirmTarget}
                    title={this._refreshConfirmText().title}
                    question={this._refreshConfirmText().question}
                    isSaving={!!this.state.busyId}
                    onYesAnswer={() => this.handleRefreshUrl(this.state.confirmTarget.doc, true)}
                    onNoAnswer={() => this.setState({confirmTarget: null})}
                />
            </div>
        );
    }
}

export const MemoryDocumentsView = withTranslation()(MemoryDocumentsViewClass);
